/**
 * Composant: Location Step Onboarding
 * Description: Étape d'onboarding pour la sélection de la localisation utilisateur
 * Usage: Intégré dans le flow d'onboarding des nouveaux utilisateurs
 */

import {
  Component,
  OnInit,
  output,
  inject,
  ChangeDetectorRef,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router } from '@angular/router';

import { LocationService } from '../../../services/location.service';
import { LocationPickerComponent } from '../../../shared/components/location-picker/location-picker.component';
import { ProgressBarComponent } from '../../../shared/components/progress-bar/progress-bar.component';
import { ValidatedAddress } from 'src/app/models/location.models';
import { OnboardingStateService } from 'src/app/services/onboarding-state.service';
import { AuthService } from 'src/app/services/auth.service';
import { LogService } from '../../../services/log.service';

/**
 * Interface pour l'événement de complétion
 */
export interface OnboardingLocationComplete {
  address: ValidatedAddress;
}

@Component({
  selector: 'app-location-step',
  imports: [CommonModule, LocationPickerComponent, ProgressBarComponent],
  templateUrl: './location-step.component.html',
  styleUrls: ['./location-step.component.css'],
})
export class LocationStepComponent implements OnInit {
  private log = inject(LogService);
  /**
   * Événement émis lorsque l'étape est complétée
   */
  readonly stepCompleted = output<OnboardingLocationComplete>();

  /**
   * Événement émis pour passer l'étape
   */
  readonly stepSkipped = output<void>();

  // États du composant
  selectedAddress: ValidatedAddress | null = null;

  private cdr = inject(ChangeDetectorRef);

  constructor(
    private locationService: LocationService,
    private onboardingStateService: OnboardingStateService,
    private authService: AuthService,
    private router: Router,
  ) {}

  ngOnInit(): void {
    this.log.debug('\n🟣🟣🟣 [LocationStep v4] CODE CHARGE 🟣🟣🟣');
    // Charger l'adresse sauvegardée si elle existe
    const data = this.onboardingStateService.getFormData();
    this.log.debug(
      '🔵 [LocationStep] adresse actuelle en sessionStorage:',
      JSON.stringify(
        data?.address?.formatted || data?.address?.address || 'aucune',
      ),
    );
    if (data?.address) {
      this.selectedAddress = data.address;
    }
  }

  /**
   * Callback quand l'utilisateur sélectionne une adresse
   * Sauvegarde immédiatement dans sessionStorage pour éviter la perte
   */
  onAddressSelected(address: ValidatedAddress): void {
    this.log.debug('\n===== 📍 ADDRESS SELECTED (v4) =====');
    this.log.debug('address.address:', address.address);
    this.log.debug('address.formatted:', address.formatted);
    this.log.debug('address.city:', address.city);
    this.log.debug('address.postalCode:', address.postalCode);
    this.log.debug('address.coordinates:', address.coordinates);
    this.log.debug('=========================================\n');
    this.selectedAddress = address;
    // Sauvegarder immédiatement dans le state (sessionStorage)
    this.onboardingStateService.updateFormData({ address });
  }

  /**
   * Passer cette étape
   */
  skipStep(): void {
    // Naviguer vers l'étape suivante (step 4 - contexte)
    const merchantType = this.onboardingStateService.getMerchantType();
    const suffix = merchantType === 'itinerant' ? 'itinerant' : 'fixed';
    this.router.navigate([`/onboarding/step/4-${suffix}`]);
  }

  /**
   * Retour à l'étape précédente
   */
  goBack(): void {
    this.router.navigate(['/onboarding/step/2']);
  }

  /**
   * Continuer vers l'étape suivante
   */
  continue(): void {
    if (this.selectedAddress) {
      // 1. Sauvegarder l'adresse dans le state (sessionStorage)
      this.onboardingStateService.updateFormData({
        address: this.selectedAddress,
      });

      // Émettre l'événement
      this.stepCompleted.emit({
        address: this.selectedAddress,
      });

      // 2. Sauvegarder l'adresse en DB puis lancer l'enrichissement (arrière-plan)
      const user = this.authService.getCurrentUser();
      const userId = (user as any)?.id || (user as any)?._id;
      const addr = this.selectedAddress;

      if (userId) {
        // D'abord sauvegarder l'adresse sélectionnée en DB
        this.locationService
          .updateLocationProfile(userId, {
            address: addr.formatted || addr.address,
            city: addr.city || '',
            postalCode: addr.postalCode || '',
            country: addr.country || 'France',
            coordinates: addr.coordinates,
          })
          .subscribe({
            next: () => {
              this.log.debug(
                '✅ Adresse sauvegardée en DB:',
                addr.formatted || addr.address,
              );
              // Puis lancer l'enrichissement en arrière-plan
              this.locationService
                .enrichLocation(
                  addr.coordinates.lat,
                  addr.coordinates.lng,
                  undefined,
                  undefined,
                  addr.formatted || addr.address,
                )
                .subscribe({
                  next: (result) =>
                    this.log.debug('✅ Enrichissement terminé:', result),
                  error: (err) =>
                    this.log.warn(
                      '⚠️ Enrichissement échoué (non bloquant):',
                      err,
                    ),
                });
            },
            error: (err) => {
              this.log.warn(
                '⚠️ Sauvegarde adresse échouée, enrichissement direct:',
                err,
              );
              // Fallback : tenter l'enrichissement quand même
              this.locationService
                .enrichLocation(
                  addr.coordinates.lat,
                  addr.coordinates.lng,
                  undefined,
                  undefined,
                  addr.formatted || addr.address,
                )
                .subscribe({
                  next: (result) =>
                    this.log.debug('✅ Enrichissement terminé:', result),
                  error: (e) => this.log.warn('⚠️ Enrichissement échoué:', e),
                });
            },
          });
      } else {
        // Pas de userId (ne devrait pas arriver) — enrichir quand même
        this.locationService
          .enrichLocation(
            addr.coordinates.lat,
            addr.coordinates.lng,
            undefined,
            undefined,
            addr.formatted || addr.address,
          )
          .subscribe({
            next: (result) => this.log.debug('✅ Enrichissement terminé:', result),
            error: (err) => this.log.warn('⚠️ Enrichissement échoué:', err),
          });
      }

      // 3. Naviguer immédiatement vers l'étape suivante (step 4 - contexte)
      const merchantType = this.onboardingStateService.getMerchantType();
      const suffix = merchantType === 'itinerant' ? 'itinerant' : 'fixed';
      this.router.navigate([`/onboarding/step/4-${suffix}`]);
    }
  }
}
